<template>
  <br v-if="tag === 'br'" />
  <component :is="tag" v-else-if="tag" :data-id="node.id" v-bind="properties">
    <template v-if="spans">
      <span
        v-for="span in spans"
        :key="span.id"
        class="relative"
        :class="span.startsHere.length === 0 && makeClasses(span.entries)"
        :data-offset="span.offset"
        @contextmenu.prevent="$emit('contextMenu', span.entries)"
        @click="handleErase(span, $event)"
      >
        <template v-if="span.startsHere.length === 0">
          {{ span.text }}
        </template>
        <template v-else>
          <span :class="makeClasses(span.entries)">
            {{ span.text }}
          </span>
          <div class="absolute -left-1 top-0 -mt-6">
            <IconWrapper
              :entries="span.startsHere"
              :legalText="legalText"
              :legalTextParagraph="legalTextParagraph"
              @delete="$emit('delete', $event)"
              @move="$emit('move', $event)"
              @editLink="$emit('editLink', $event)"
            />
          </div>
        </template>
      </span>
    </template>
    <template v-else>
      {{ text }}
    </template>
  </component>
</template>

<script lang="ts" setup>
import type { IParagraphNode } from "~/models/IParagraphNode";
import type {
  ILinkChain,
  INote,
  IParagraphEntry,
  ISchema,
} from "~/models/IUserEntry";
import type { ILegalText } from "~/models/ILegalText";
import type { ILegalTextParagraph } from "~/models/ILegalTextParagraph";

interface Props {
  node: IParagraphNode & { entries?: IParagraphEntry[] };
  legalText: ILegalText;
  legalTextParagraph: ILegalTextParagraph;
}

const props = defineProps<Props>();

type Emits = {
  delete: [entry: IParagraphEntry];
  move: [entry: ILinkChain | INote | ISchema];
  editLink: [entry: ILinkChain];
  contextMenu: [entries: IParagraphEntry[]];
};

const emit = defineEmits<Emits>();

const { node } = toRefs(props);

const { properties, tag, text } = useNodeTransform(node.value);

const { spans } = useEntrySpans(node, text);
const { hoverToDeleteItem } = storeToRefs(useContextMenuHoverDelete());

const makeClasses = (entries: IParagraphEntry[]) => {
  const latestBg = entries
    ?.filter(({ type }) => type === "mark")
    .sort((a, b) => Date.parse(b.updated_at) - Date.parse(a.updated_at))?.[0];

  const getClass = (entry: IParagraphEntry) => {
    switch (entry.type) {
      case "underline":
        return `lexmea-underline ${hoverToDeleteItem.value === entry.id ? "!decoration-lexmea-selection-darker !decoration-4" : ""}`;
      case "mark":
        if (entry.id === latestBg?.id) {
          return hoverToDeleteItem.value === entry.id
            ? "border border-2 border-lexmea-selection-darker"
            : `bg-entry-${entry.color}-${entry.color_type}`;
        }
    }
  };
  return Array.from(
    new Set(
      entries.map((entry) => getClass(entry)).filter((v) => !!v) as string[]
    )
  );
};

const { injectErasingMode } = useEraser();
const erasingMode = injectErasingMode();

const handleErase = async (
  span: { entries: IParagraphEntry[] },
  event: Event
) => {
  if (toValue(erasingMode)) {
    event.preventDefault();
    event.stopPropagation();

    const entry = span.entries[span.entries.length - 1];

    if (entry) {
      emit("delete", entry);
    }
  }
};

//  Emit close from context entry not working on some browsers
watch(spans, () => {
  emit("contextMenu", []);
});
</script>

<style scoped>
.lexmea-underline {
  -webkit-text-decoration-line: underline;
  text-decoration-line: underline;
  text-decoration-thickness: 1pt;
  text-decoration-color: #1f2329;
}
</style>
